<template>
  <div class="error-notification-host">
    <transition name="slide-fade">
      <div
        v-if="visible"
        class="error-notification"
        :class="notificationClass"
        @mouseenter="pauseAutoClose"
        @mouseleave="resumeAutoClose"
      >
      <!-- 关闭按钮 -->
      <button class="close-btn" @click="handleClose">
        <Icon type="md-close" />
      </button>
      
      <!-- 进度条 -->
      <div class="progress-bar" :style="{ width: progressWidth + '%' }"></div>
      
      <!-- 内容区域 -->
      <div class="notification-content">
        <div class="icon-wrapper">
          <Icon type="ios-warning" />
        </div>
        
        <div class="message-wrapper">
          <div class="title">{{ displayTitle }}</div>
          <div class="description" v-html="description" @dblclick="handleImageDoubleClick"></div>
        </div>
      </div>
      
      <!-- 操作按钮 -->
      <div v-if="detailLabel || showIgnore" class="action-wrapper">
        <div v-if="detailLabel" class="detail-row">
          <button class="detail-btn" @click="handleDetail">{{ detailLabel }}</button>
        </div>
        <template v-if="showIgnore">
          <div class="error-tip" v-if="ignoreError">{{ ignoreError }}</div>
          <span class="mute-label">{{ langMap.ignore[lang] }}</span>
          <select class="ignore-select" v-model="ignoreSelectValue">
            <option value="30">30{{ langMap.second[lang] }}</option>
            <option value="60">60{{ langMap.second[lang] }}</option>
            <option value="90">90{{ langMap.second[lang] }}</option>
            <option value="120">120{{ langMap.second[lang] }}</option>
            <option value="custom">{{ langMap.customize[lang] }}</option>
          </select>
          <input
            v-if="ignoreSelectValue === 'custom'"
            v-model="customIgnoreSeconds"
            type="number"
            min="1"
            class="custom-ignore-input"
            :placeholder="langMap.second[lang]"
          />
          <button class="ignore-btn" @click="handleIgnore">
            <span class="btn-text">{{ langMap.ignore[lang] }}</span>
            <span class="btn-hint">{{ currentIgnoreSeconds }}{{ langMap.second[lang] }}</span>
          </button>
        </template>
      </div>
      </div>
    </transition>
    <div
      v-if="previewImageUrl"
      class="notification-image-preview"
      role="dialog"
      aria-modal="true"
      @click.self="closeImagePreview"
    >
      <button class="notification-image-preview-close" type="button" aria-label="Close" @click="closeImagePreview">×</button>
      <img :src="previewImageUrl" alt="" @load="syncPreviewImageSize" @click.stop />
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex';

export default {
  name: 'ErrorNotification',
  props: {
    title: {
      type: String,
      default: '' 
    },
    description: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'warning', // 'warning' | 'error' | 'emergency'
      validator: (value) => ['warning', 'error', 'emergency'].includes(value)
    },
    duration: {
      type: Number,
      default: 5000 // 自动关闭时间（毫秒）
    },
    robotId: {
      type: [String, Number],
      required: true
    },
    ignoreDefaultSeconds: {
      type: Number,
      default: 30
    },
    detailLabel: {
      type: String,
      default: ''
    },
    showIgnore: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      langMap,
      visible: false,
      progressWidth: 100,
      timer: null,
      progressTimer: null,
      closeTimer: null,
      ignoreSelectValue: '30',
      customIgnoreSeconds: '',
      ignoreError: '',
      previewImageUrl: ''
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    notificationClass() {
      return `notification-${this.type}`;
    },
    displayTitle() {
      if (this.title) return this.title;
      return this.langMap.dashboard_errorNotificationTitle[this.lang];
    },
    currentIgnoreSeconds() {
      if (this.ignoreSelectValue === 'custom') {
        const n = Number(this.customIgnoreSeconds);
        return Number.isFinite(n) && n > 0 ? n : 0;
      }
      return Number(this.ignoreSelectValue);
    }
  },
  mounted() {
    const defaultSeconds = Number(this.ignoreDefaultSeconds);
    if ([30, 60, 90, 120].includes(defaultSeconds)) {
      this.ignoreSelectValue = String(defaultSeconds);
    } else if (Number.isFinite(defaultSeconds) && defaultSeconds > 0) {
      this.ignoreSelectValue = 'custom';
      this.customIgnoreSeconds = String(defaultSeconds);
    }
    this.$nextTick(() => {
      this.show();
    });
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    handleImageDoubleClick(event) {
      let image = event && event.target;
      if (image && image.nodeType !== 1) image = image.parentElement;
      if (image && image.closest) image = image.closest('.pursuit-notification-image');
      if (image && event.currentTarget && !event.currentTarget.contains(image)) image = null;
      if (image) {
        this.previewImageUrl = image.currentSrc || image.src || '';
        this.$nextTick(this.syncPreviewImageSize);
      }
    },
    closeImagePreview() {
      this.previewImageUrl = '';
    },
    syncPreviewImageSize() {
      const image = this.$el && this.$el.querySelector('.notification-image-preview > img');
      if (!image || !image.naturalWidth || !image.naturalHeight) return;
      image.style.width = `${image.naturalWidth}px`;
      image.style.height = `${image.naturalHeight}px`;
    },
    show() {
      if (this._isBeingDestroyed || this._isDestroyed) return;
      // 重置状态
      this.progressWidth = 100;
      this.clearTimers();
      this.visible = true;
      this.$nextTick(() => {
        this.startAutoClose();
      });
    },
    startAutoClose() {
      if (!this.visible || this._isBeingDestroyed || this._isDestroyed || this.timer || this.progressTimer) return;
      if (this.duration > 0) {
        // 进度条动画
        const interval = 50; // 更新间隔（毫秒）
        const step = (interval / this.duration) * 100;
        
        this.progressTimer = setInterval(() => {
          this.progressWidth -= step;
          if (this.progressWidth <= 0) {
            this.progressWidth = 0;
            this.clearTimers();
            this.handleClose();
          }
        }, interval);
        
        // 自动关闭定时器
        this.timer = setTimeout(() => {
          this.handleClose();
        }, this.duration);
      }
    },
    clearTimers() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    },
    pauseAutoClose() {
      if (this.visible) this.clearTimers();
    },
    resumeAutoClose() {
      if (this.visible && this.progressWidth > 0) {
        this.startAutoClose();
      }
    },
    handleClose() {
      this.clearTimers();
      this.visible = false;
      this.closeTimer = setTimeout(() => {
        this.$emit('close');
      }, 300); // 等待动画结束
    },
    handleIgnore() {
      const seconds = this.currentIgnoreSeconds;
      if (!seconds) {
        this.ignoreError = this.langMap.PleaseInputTime[this.lang];
        return;
      }
      this.ignoreError = '';
      this.clearTimers();
      this.visible = false;
      this.closeTimer = setTimeout(() => {
        this.$emit('ignore', this.robotId, seconds);
      }, 300);
    },
    handleDetail() {
      this.clearTimers();
      this.visible = false;
      this.closeTimer = setTimeout(() => {
        this.$emit('detail');
      }, 300);
    }
  }
}
</script>

<style lang="less" scoped>
.error-notification {
  position: fixed;
  top: 80px;
  right: 20px;
  width: 420px;
  background: linear-gradient(135deg, rgba(0, 21, 41, 0.98), rgba(0, 40, 80, 0.95));
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 
              0 0 20px rgba(255, 152, 0, 0.3),
              inset 0 0 20px rgba(255, 152, 0, 0.05);
  border: 2px solid rgba(255, 152, 0, 0.4);
  z-index: 99;
  overflow: hidden;
  backdrop-filter: blur(10px);
  
  // 进度条
  .progress-bar {
    position: absolute;
    top: 0;
    left: 0;
    height: 3px;
    background: linear-gradient(90deg, #FF9800, #FFA726, #FF9800);
    transition: width 0.05s linear;
    box-shadow: 0 0 10px rgba(255, 152, 0, 0.8);
  }
  
  // 关闭按钮
  .close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 28px;
    height: 28px;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    font-size: 20px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.3s ease;
    z-index: 10;
    padding: 0;
    outline: none;
    
    /deep/ i {
      font-size: 20px;
      line-height: 1;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.15);
      color: rgba(255, 255, 255, 1);
      transform: rotate(90deg);
    }
  }
  
  // 内容区域
  .notification-content {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 16px;
    
    .icon-wrapper {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(255, 152, 0, 0.2), rgba(255, 193, 7, 0.15));
      border: 2px solid rgba(255, 152, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 0 15px rgba(255, 152, 0, 0.4);
      
      /deep/ i {
        font-size: 24px;
        color: #FF9800;
        filter: drop-shadow(0 0 5px rgba(255, 152, 0, 0.8));
        animation: iconPulse 2s ease-in-out infinite;
        line-height: 1;
      }
    }
    
    .message-wrapper {
      flex: 1;
      padding-right: 24px; // 为关闭按钮留空间
      
      .title {
        font-size: 16px;
        font-weight: 600;
        color: #fff;
        margin-bottom: 8px;
        line-height: 1.4;
      }
      
      .description {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.75);
        line-height: 1.6;
      }
    }
  }
  
  // 操作区域
  .action-wrapper {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 10px;
    position: relative;
    margin-top: 4px;

    .detail-row {
      flex: 0 0 100%;
      width: 100%;
      display: flex;
      justify-content: center;
      margin-bottom: 2px;
    }

    .detail-btn {
      height: 34px;
      padding: 0 13px;
      border: 1px solid rgba(0, 220, 232, .72);
      border-radius: 6px;
      color: #dffcff;
      background: rgba(0, 150, 170, .2);
      cursor: pointer;
      font-size: 13px;
      font-weight: 600;
      white-space: nowrap;
    }
    .detail-btn:hover {
      border-color: #00f0ff;
      background: rgba(0, 190, 210, .35);
      box-shadow: 0 0 10px rgba(0, 240, 255, .28);
    }

    .error-tip {
      position: absolute;
      top: -18px;
      left: 0;
      right: 0;
      text-align: center;
      color: #ed4014;
      font-size: 12px;
    }

    .mute-label {
      color: rgba(255, 255, 255, 0.86);
      font-size: 12px;
      white-space: nowrap;
      font-weight: 500;
      letter-spacing: 0.5px;
    }

    .ignore-select {
      height: 34px;
      min-width: 96px;
      padding: 0 10px;
      border-radius: 6px;
      border: 1px solid rgba(255, 255, 255, 0.24);
      background: rgba(0, 0, 0, 0.18);
      color: #fff;
      outline: none;
      cursor: pointer;
      font-size: 13px;
      transition: all 0.2s ease;
    }

    .ignore-select:hover,
    .ignore-select:focus {
      border-color: rgba(255, 255, 255, 0.42);
      background: rgba(0, 0, 0, 0.28);
    }

    .ignore-select option {
      background: #132a49;
      color: #fff;
    }

    .custom-ignore-input {
      width: 68px;
      height: 34px;
      padding: 0 8px;
      border-radius: 6px;
      border: 1px solid rgba(255, 255, 255, 0.24);
      background: rgba(0, 0, 0, 0.18);
      color: #fff;
      outline: none;
      font-size: 13px;
      text-align: center;
      transition: all 0.2s ease;
      -moz-appearance: textfield;
      appearance: textfield;
    }

    .custom-ignore-input:hover,
    .custom-ignore-input:focus {
      border-color: rgba(255, 255, 255, 0.42);
      background: rgba(0, 0, 0, 0.28);
    }

    .custom-ignore-input::-webkit-outer-spin-button,
    .custom-ignore-input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    
    .ignore-btn {
      display: inline-flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;
      padding: 0 14px;
      height: 34px;
      background: linear-gradient(135deg, rgba(255, 152, 0, 0.28), rgba(255, 152, 0, 0.16));
      border: 1px solid rgba(255, 152, 0, 0.58);
      border-radius: 6px;
      color: #fff;
      cursor: pointer;
      transition: all 0.2s ease;
      
      .btn-text {
        font-size: 13px;
        font-weight: 600;
      }
      
      .btn-hint {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.88);
      }
      
      &:hover {
        background: linear-gradient(135deg, rgba(255, 152, 0, 0.38), rgba(255, 152, 0, 0.24));
        border-color: rgba(255, 152, 0, 0.8);
        box-shadow: 0 0 10px rgba(255, 152, 0, 0.3);
        transform: translateY(-1px);
      }
      
      &:active {
        transform: translateY(0);
      }
    }
  }

  // 错误和紧急状态的样式覆盖
  &.notification-error,
  &.notification-emergency {
    border-color: rgba(244, 67, 54, 0.5);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 
                0 0 20px rgba(244, 67, 54, 0.4),
                inset 0 0 20px rgba(244, 67, 54, 0.05);

    .progress-bar {
      background: linear-gradient(90deg, #F44336, #EF5350, #F44336);
      box-shadow: 0 0 10px rgba(244, 67, 54, 0.8);
    }

    .notification-content .icon-wrapper {
      background: linear-gradient(135deg, rgba(244, 67, 54, 0.2), rgba(229, 57, 53, 0.15));
      border-color: rgba(244, 67, 54, 0.5);
      box-shadow: 0 0 15px rgba(244, 67, 54, 0.4);
      
      /deep/ i {
        color: #F44336;
        filter: drop-shadow(0 0 5px rgba(244, 67, 54, 0.8));
      }
    }

    .action-wrapper .ignore-btn {
      background: rgba(244, 67, 54, 0.15);
      border-color: rgba(244, 67, 54, 0.4);
      color: #F44336;
      
      .btn-hint {
        color: rgba(244, 67, 54, 0.7);
      }
      
      &:hover {
        background: rgba(244, 67, 54, 0.25);
        border-color: rgba(244, 67, 54, 0.6);
        box-shadow: 0 0 15px rgba(244, 67, 54, 0.3);
      }
    }

    .action-wrapper .ignore-select {
      border-color: rgba(244, 67, 54, 0.55);
      background: rgba(244, 67, 54, 0.16);
      color: #fff;
    }

    .action-wrapper .ignore-select option {
      background: #3b1a1a;
      color: #fff;
    }

    .action-wrapper .custom-ignore-input {
      border-color: rgba(244, 67, 54, 0.55);
      background: rgba(244, 67, 54, 0.16);
      color: #fff;
    }

    .action-wrapper .mute-label {
      color: rgba(255, 255, 255, 0.88);
    }
  }
}

// 动画
.slide-fade-enter-active {
  transition: all 0.4s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.notification-image-preview {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  overflow: auto;
  padding: 72px 34px 34px;
  background: rgba(1, 12, 24, .9);
  backdrop-filter: blur(6px);
}
.notification-image-preview > img {
  display: block;
  flex: 0 0 auto;
  max-width: none;
  max-height: none;
  width: auto;
  height: auto;
  object-fit: contain;
  margin: auto;
  border: 1px solid rgba(255, 183, 73, .75);
  border-radius: 8px;
  box-shadow: 0 0 24px rgba(255, 152, 0, .3), 0 18px 60px rgba(0, 0, 0, .6);
}
.pursuit-notification-image {
  cursor: zoom-in;
}
.notification-image-preview > img {
  cursor: zoom-out;
}
.notification-image-preview-close {
  position: fixed;
  top: 16px;
  right: 20px;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(255, 183, 73, .65);
  border-radius: 50%;
  color: #fff3d6;
  background: rgba(67, 42, 8, .9);
  font-size: 24px;
  line-height: 28px;
  cursor: pointer;
  z-index: 2001;
}
.notification-image-preview-close:hover {
  border-color: #ffc15c;
  box-shadow: 0 0 12px rgba(255, 152, 0, .5);
}
</style>
